// src/components/Footer.tsx
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  BUSINESS_ID_ABS,
  SEO_SERVICE_BAR_BAT,
  SEO_SERVICE_EVENT,
  SEO_SERVICE_HENNA,
  SEO_SERVICE_WEDDING,
  withBet,
  type SeoService,
} from '@/constants/core';
import {
  globalTypePath,
  typeLocationPath,
  type EventType,
  type Region,
} from '@/constants/geo';
import { PLANNING_GUIDE_PATH } from '@/constants/event-planning';
import { MEDIA_BREAKPOINTS } from '@/constants/layout';
import { useI18n } from '@/i18n/LanguageProvider';

import { InternalLinksSection } from '@/components/InternalLinks/InternalLinks';
import SocialMediaMenu from '@/components/SocialMediaMenu';
import { type InternalLink } from '@/utils/internalLinks';
import type { Fonts } from '@/theme/themes';

export function orderLinksForColumns(
  links: InternalLink[],
  columns: number,
): InternalLink[] {
  if (!Array.isArray(links) || links.length === 0 || columns <= 1) return links;

  // תופס: "בר/בת מצווה" וגם "בר-מצווה" (וגם אם יש תווים מיוחדים)
  const isBarBat = (l: InternalLink) =>
    /בר\s*[-/–]?\s*בת\s*מצווה|בר[-\s]?מצווה|בת[-\s]?מצווה/.test(l.label);
  const barBat = links.filter(isBarBat);
  const others = links.filter(l => !isBarBat(l));

  const total = links.length;
  const rows = Math.ceil(total / columns);

  // "הטור האחרון" בגריד שממלא שורות = אינדקסים: (columns-1), (2*columns-1), ...
  const lastCol = columns - 1;

  const out: Array<InternalLink | null> = Array(total).fill(null);

  // 1) קודם כל משבצים בר/בת מצווה בטור האחרון, מלמעלה למטה
  let bi = 0;
  for (let r = 0; r < rows && bi < barBat.length; r++) {
    const idx = r * columns + lastCol;
    if (idx < total) out[idx] = barBat[bi++];
  }

  // 2) ממלאים את שאר המקומות לפי סדר (row-major) עם כל שאר הקישורים
  let oi = 0;
  for (let i = 0; i < total; i++) {
    if (out[i]) continue;
    out[i] = others[oi++] ?? null;
  }

  // 3) אם נשארו בר/בת מצווה מעבר לגובה הטור האחרון (נדיר), דוחפים לסוף
  while (bi < barBat.length) {
    out.push(barBat[bi++]);
  }

  return out.filter(Boolean) as InternalLink[];
}

type FooterVariant = 'default' | 'planning';

type FooterProps = {
  footerLinks?: InternalLink[];
  footerHeading?: string;
  region?: Region; // 👈 חדש
  showPlanningParentLinks?: boolean;
  planningCity?: string;
  planningEventType?: EventType;
  variant?: FooterVariant;
  showFeaturedPlanningGuideLink?: boolean;
};

const PLANNING_REGION_LINK_ORDER: readonly Region[] = ['דרום', 'צפון', 'מרכז'];

const StyledFooter = styled.footer.attrs({
  role: 'contentinfo',
  itemScope: true,
  itemType: 'https://schema.org/LocalBusiness',
  itemID: BUSINESS_ID_ABS,
})<{ $variant: FooterVariant }>`
  background: ${({ theme, $variant }) =>
    $variant === 'planning' ? 'rgba(18, 19, 26, 0.36)' : theme.menuColor};
  color: ${({ theme, $variant }) =>
    $variant === 'planning' ? '#ffffff' : theme.descriptionColor};
  padding: 30px 12px 20px 20px;
  border-top: ${({ $variant }) =>
    $variant === 'planning' ? '1px solid rgba(184, 151, 255, 0.38)' : '0'};
  box-shadow: ${({ $variant }) =>
    $variant === 'planning'
      ? 'inset 0 18px 36px rgba(216, 199, 255, 0.03)'
      : 'none'};
  backdrop-filter: ${({ $variant }) =>
    $variant === 'planning' ? 'blur(10px)' : 'none'};
`;

const Layout = styled.div<{ $hasPlanningParentLinks?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $hasPlanningParentLinks }) =>
    $hasPlanningParentLinks ? 'minmax(0, 940px)' : '1fr 1fr'};
  justify-content: ${({ $hasPlanningParentLinks }) =>
    $hasPlanningParentLinks ? 'center' : 'stretch'};
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

/* ===== צד ימין — קישורים ===== */

const LinksWrap = styled.div<{
  $align: 'right' | 'left';
  $variant: FooterVariant;
}>`
  text-align: ${({ $align }) => $align};
  direction: ${({ $align }) => ($align === 'left' ? 'ltr' : 'rtl')};
  width: 100%;

  &:empty {
    display: none;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    order: ${({ $variant }) => ($variant === 'planning' ? 0 : 2)};
  }

  ${({ $variant }) =>
    $variant === 'planning'
      ? `
    && nav h3 {
      color: #d8c7ff;
    }

    && nav a {
      color: #ffffff;
    }

    && nav a:hover,
    && nav a:focus-visible {
      color: #d8c7ff;
      text-decoration-color: #b897ff;
    }
  `
      : ''}
`;

/* ===== צד שמאל — מפה ===== */

const MapWrap = styled.div<{ $center?: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  gap: clamp(18px, 3vw, 32px);
  direction: ltr;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    order: 1;
    flex-direction: column;
    gap: 14px;
    align-items: stretch;
  }
`;

const MapContent = styled.div`
  flex: 0 1 500px;
  min-width: 0;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    flex-basis: auto;
  }
`;

const MapHeading = styled.h2<{ $variant: FooterVariant }>`
  margin: 0 0 8px;
  text-align: right;
  direction: rtl;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: 0.95rem;
  line-height: 1.25;
  color: ${({ theme, $variant }) =>
    $variant === 'planning' ? '#d8c7ff' : theme.descriptionColor};
`;

const MapFrame = styled.iframe<{ $variant: FooterVariant }>`
  width: 100%;
  box-sizing: border-box;
  height: 220px;
  border: ${({ $variant }) =>
    $variant === 'planning' ? '1px solid rgba(184, 151, 255, 0.42)' : '0'};
  border-radius: 8px;
  box-shadow: ${({ $variant }) =>
    $variant === 'planning' ? '0 14px 28px rgba(0, 0, 0, 0.28)' : 'none'};
`;

const AddressText = styled.div<{ $variant: FooterVariant }>`
  margin-top: 8px;
  font-size: 0.9rem;

  /* ✅ יישור לימין */
  text-align: right;

  /* ✅ פונט מה-theme — אחיד עם האתר */
  font-family: ${({ theme }) => theme.fonts.rubikBoldItalic};

  /* אופציונלי — צבע יותר עדין */
  color: ${({ theme, $variant }) =>
    $variant === 'planning' ? '#e2dcec' : theme.descriptionColor};
`;

const FooterSocialWrap = styled.div<{ $variant: FooterVariant }>`
  display: ${({ $variant }) => ($variant === 'planning' ? 'flex' : 'none')};
  justify-content: center;
  align-self: center;
  width: ${({ $variant }) =>
    $variant === 'planning' ? 'min(100%, 320px)' : '100%'};
  padding: ${({ $variant }) =>
    $variant === 'planning' ? '10px 0 0' : '4px 0 2px'};

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: flex;
    width: 100%;
    padding: 4px 0 2px;
  }
`;

/* ===== טקסט תחתון ===== */

const FooterText = styled.p<{
  $fontFamily?: keyof Fonts;
  $variant: FooterVariant;
}>`
  margin-top: 24px;
  text-align: center;
  font-size: 0.85rem;
  line-height: 1.4;
  color: ${({ theme, $variant }) =>
    $variant === 'planning' ? '#e2dcec' : theme.descriptionColor};

  font-family: ${({ theme, $fontFamily }) =>
    $fontFamily ? theme.fonts[$fontFamily] : theme.fonts.rubikExtraBold};
`;

const FooterLegalLinks = styled.nav<{ $variant: FooterVariant }>`
  margin-top: 10px;
  text-align: center;
  font-size: 0.85rem;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};

  a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 4px 6px;
    box-sizing: border-box;
    color: ${({ $variant }) =>
      $variant === 'planning' ? '#d8c7ff' : 'inherit'};
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

const FeaturedGuideNav = styled.nav<{
  $variant: FooterVariant;
  $hasLinks: boolean;
}>`
  width: 100%;
  margin: ${({ $hasLinks }) => ($hasLinks ? '0 0 14px' : '0')};
  direction: inherit;
  display: flex;
  justify-content: center;
  text-align: center;

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    max-width: 100%;
    box-sizing: border-box;
    padding: 9px 14px;
    border-radius: 8px;
    color: ${({ $variant }) =>
      $variant === 'planning' ? '#ffffff' : '#d8c7ff'};
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: 0.95rem;
    line-height: 1.2;
    text-align: center;
    text-decoration: none;
    background: ${({ $variant }) =>
      $variant === 'planning'
        ? 'rgba(184, 151, 255, 0.12)'
        : 'rgba(18, 19, 26, 0.18)'};
    border: 1px solid rgba(184, 151, 255, 0.5);
    box-shadow: ${({ $variant }) =>
      $variant === 'planning'
        ? '0 10px 22px rgba(0, 0, 0, 0.24)'
        : '0 8px 18px rgba(0, 0, 0, 0.16)'};
  }

  a:hover,
  a:focus-visible {
    color: #ffffff;
    background: rgba(184, 151, 255, 0.18);
    border-color: rgba(216, 199, 255, 0.72);
    outline: 2px solid rgba(184, 151, 255, 0.34);
    outline-offset: 3px;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    text-align: center;
  }
`;

const FooterParentLinks = styled.nav<{
  $variant: FooterVariant;
  $count: number;
}>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ $variant, $count }) =>
    $variant === 'planning'
      ? `repeat(${Math.max(1, Math.min($count, 4))}, minmax(0, 1fr))`
      : 'repeat(4, max-content)'};
  justify-content: center;
  gap: 8px 18px;
  font-size: 0.9rem;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  direction: rtl;
  text-align: right;
  max-width: ${({ $variant, $count }) =>
    $variant === 'planning' ? ($count <= 3 ? '620px' : '760px') : 'none'};
  margin: ${({ $variant }) => ($variant === 'planning' ? '0 auto' : '0')};

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: ${({ $variant, $count }) =>
      $variant === 'planning'
        ? `repeat(${Math.max(1, Math.min($count, 2))}, minmax(0, 1fr))`
        : 'repeat(2, max-content)'};
    justify-content: center;
    margin: 0 auto;
    gap: 8px;
  }

  a {
    display: ${({ $variant }) =>
      $variant === 'planning' ? 'inline-flex' : 'inline'};
    align-items: center;
    justify-content: center;
    min-height: ${({ $variant }) =>
      $variant === 'planning' ? '40px' : 'auto'};
    padding: ${({ $variant }) => ($variant === 'planning' ? '8px 12px' : '0')};
    border-radius: ${({ $variant }) => ($variant === 'planning' ? '8px' : '0')};
    color: ${({ $variant }) =>
      $variant === 'planning' ? '#d8c7ff' : 'inherit'};
    text-align: center;
    text-decoration: ${({ $variant }) =>
      $variant === 'planning' ? 'none' : 'underline'};
    text-underline-offset: 4px;
    background: ${({ $variant }) =>
      $variant === 'planning' ? 'rgba(18, 19, 26, 0.24)' : 'transparent'};
    border: ${({ $variant }) =>
      $variant === 'planning' ? '1px solid rgba(184, 151, 255, 0.42)' : '0'};
    box-shadow: ${({ $variant }) =>
      $variant === 'planning' ? '0 8px 18px rgba(0, 0, 0, 0.22)' : 'none'};
    white-space: normal;
  }

  ${({ $variant }) =>
    $variant === 'planning'
      ? `
    a:hover,
    a:focus-visible {
      color: #ffffff;
      background: rgba(184, 151, 255, 0.12);
      border-color: rgba(216, 199, 255, 0.66);
      outline: 2px solid rgba(184, 151, 255, 0.32);
      outline-offset: 3px;
    }
  `
      : ''}
`;

/* ===== Hidden Schema Meta ===== */

const HiddenMeta = styled.span`
  display: none;
`;

const normalizeFooterPath = (href: string) => {
  const value = String(href ?? '').trim();
  if (!value) return '';

  try {
    const url = new URL(value, 'https://www.djeliormor.co.il');
    const path = url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`;
    return path;
  } catch {
    return value.endsWith('/') ? value : `${value}/`;
  }
};

const Footer: React.FC<FooterProps> = ({
  footerLinks,
  region = 'מרכז',
  showPlanningParentLinks = false,
  planningCity,
  planningEventType,
  variant = 'default',
  showFeaturedPlanningGuideLink = true,
}) => {
  const { i18n, lang } = useI18n();
  const mapContentRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);

  const headingText = i18n.footer.recommendedPages;
  const addressLabel = i18n.footer.addressLabel;
  const addresses = i18n.footer.addresses;
  const footerText = i18n.footer.copyright(new Date().getFullYear());

  const mapData =
    region === 'דרום'
      ? {
          address: addresses.south,
          src: 'https://www.google.com/maps?q=מבצע+יואב+16+באר+שבע&output=embed',
        }
      : {
          address: addresses.center,
          src: 'https://www.google.com/maps?q=מגדלי+אלון+2+תל+אביב&output=embed',
        };

  useEffect(() => {
    if (shouldLoadMap) return;

    const target = mapContentRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      setShouldLoadMap(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoadMap(true);
        observer.disconnect();
      },
      { rootMargin: '1600px 0px' },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [shouldLoadMap]);

  const isMobile =
    typeof window !== 'undefined' &&
    window.innerWidth <= parseInt(MEDIA_BREAKPOINTS.mediumScreen);

  const cols = isMobile ? 2 : 3;

  const recommendedLinks = footerLinks
    ? footerLinks.filter(
        link => normalizeFooterPath(link.href) !== PLANNING_GUIDE_PATH,
      )
    : [];
  const orderedLinks = orderLinksForColumns(recommendedLinks, cols);
  const footerLinksAlign = lang === 'he' ? 'right' : 'left';

  const parentLinks: Array<{ service: SeoService; type: EventType }> = [
    { service: SEO_SERVICE_WEDDING, type: 'חתונה' },
    { service: SEO_SERVICE_HENNA, type: 'חינה' },
    { service: SEO_SERVICE_BAR_BAT, type: 'בר-מצווה' },
    { service: SEO_SERVICE_EVENT, type: 'אירוע' },
  ];
  const planningCityName = String(planningCity ?? '').trim();
  const planningCityLabel = planningCityName
    ? i18n.translateCity(planningCityName)
    : '';

  const cityScopedFooterLabel = (service: SeoService) => {
    const serviceLabel = i18n.preferredKeywordForService(service);
    if (!planningCityName) return serviceLabel;
    if (lang === 'he') return `${serviceLabel}${withBet(planningCityName)}`;

    return `${serviceLabel} - ${planningCityLabel}`;
  };

  const cityScopedFooterHref = (type: EventType) =>
    planningCityName
      ? typeLocationPath(type, planningCityName)
      : globalTypePath(type);

  const regionScopedFooterLabel = (type: EventType, regionName: Region) => {
    const service =
      parentLinks.find(item => item.type === type)?.service ??
      SEO_SERVICE_EVENT;
    const serviceLabel = i18n.preferredKeywordForService(service);
    if (lang === 'he') return `${serviceLabel}${withBet(regionName)}`;

    return `${serviceLabel} - ${i18n.translateRegion(regionName)}`;
  };

  const planningParentLinks = planningCityName
    ? parentLinks.map(item => ({
        key: `city-${item.type}`,
        href: cityScopedFooterHref(item.type),
        label: cityScopedFooterLabel(item.service),
      }))
    : planningEventType
      ? PLANNING_REGION_LINK_ORDER.map(regionName => ({
          key: `region-${planningEventType}-${regionName}`,
          href: typeLocationPath(planningEventType, regionName),
          label: regionScopedFooterLabel(planningEventType, regionName),
        }))
      : parentLinks.map(item => ({
          key: `global-${item.type}`,
          href: cityScopedFooterHref(item.type),
          label: cityScopedFooterLabel(item.service),
        }));

  return (
    <StyledFooter aria-label="Footer" $variant={variant}>
      {/* ===== Schema.org ===== */}
      <HiddenMeta itemProp="name">DJ Elior Mor</HiddenMeta>

      <Layout $hasPlanningParentLinks={showPlanningParentLinks}>
        {/* ===== צד שמאל — קישורים ===== */}
        <LinksWrap $align={footerLinksAlign} $variant={variant}>
          {showFeaturedPlanningGuideLink && (
            <FeaturedGuideNav
              aria-label={i18n.PLANNING_GUIDE_TITLE}
              $variant={variant}
              $hasLinks={orderedLinks.length > 0}
            >
              <a
                href={PLANNING_GUIDE_PATH}
                title={i18n.PLANNING_GUIDE_SUBTITLE}
              >
                {i18n.PLANNING_GUIDE_TITLE}
              </a>
            </FeaturedGuideNav>
          )}

          {orderedLinks.length > 0 && (
            <InternalLinksSection
              heading={headingText}
              links={orderedLinks}
              columns={cols} // לא משנה כבר
              align={footerLinksAlign}
            />
          )}
        </LinksWrap>

        {showPlanningParentLinks && (
          <FooterParentLinks
            aria-label={i18n.PLANNING_GUIDE_TITLE}
            $variant={variant}
            $count={planningParentLinks.length}
          >
            {planningParentLinks.map(item => (
              <a key={item.key} href={item.href}>
                {item.label}
              </a>
            ))}
          </FooterParentLinks>
        )}

        {/* ===== צד ימין — מפה ===== */}
        <MapWrap $center={showPlanningParentLinks}>
          <MapContent ref={mapContentRef}>
            <MapHeading $variant={variant}>{addressLabel}</MapHeading>
            <MapFrame
              title={`${addressLabel} - ${mapData.address}`}
              src={shouldLoadMap ? mapData.src : undefined}
              loading="eager"
              referrerPolicy="no-referrer-when-downgrade"
              $variant={variant}
            />
            <AddressText $variant={variant}>{mapData.address}</AddressText>
          </MapContent>
          <FooterSocialWrap $variant={variant}>
            <SocialMediaMenu
              orientation="horizontal"
              showLabels
              labelTone={variant === 'planning' ? 'planning' : 'default'}
            />
          </FooterSocialWrap>
        </MapWrap>
      </Layout>

      <FooterText $variant={variant}>{footerText}</FooterText>
      <FooterLegalLinks aria-label="קישורים משפטיים" $variant={variant}>
        <a href="/הצהרת-נגישות/">{i18n.footer.accessibilityStatement}</a>
      </FooterLegalLinks>
    </StyledFooter>
  );
};

export default Footer;
